import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const CATEGORIES = ["Hospitals", "Discharge Planners", "Social Workers", "Tailored Care Managers", "Care Managers",
  "Guardians", "Elder-Law Attorneys", "Hospice", "Home Health", "Skilled Nursing / Rehab", "Physicians",
  "Churches", "Veterans Organizations", "Senior Centers", "Realtors", "Senior Transition Companies",
  "Community Organizations", "Other"];

export default function ReferralSources() {
  const [refs, setRefs] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { organization: "", contact: "", email: "", phone: "", location: "", category: "Hospitals",
    last_contact: "", next_follow_up: "", status: "New", notes: "" };
  const [f, setF] = useState(empty);
  const load = () => api.get("/agent/referral-sources").then((r) => setRefs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/agent/referral-sources", f); toast.success("Referral source added"); setOpen(false); setF(empty); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/agent/referral-sources/${id}`); load(); };

  return (
    <div>
      <PageTitle title="My Referral Sources" subtitle="Your professional referral CRM">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-ref-btn" className="bg-navy hover:bg-navy/90"><Plus className="mr-2 h-4 w-4" /> Add Source</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Add referral source</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="ref-form">
              <FF label="Organization" req><Input data-testid="ref-org" required value={f.organization} onChange={set("organization")} /></FF>
              <div className="grid gap-4 sm:grid-cols-2">
                <FF label="Category">
                  <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                    <SelectTrigger data-testid="ref-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Contact"><Input data-testid="ref-contact" value={f.contact} onChange={set("contact")} /></FF>
                <FF label="Email"><Input data-testid="ref-email" value={f.email} onChange={set("email")} /></FF>
                <FF label="Phone"><Input data-testid="ref-phone" value={f.phone} onChange={set("phone")} /></FF>
                <FF label="Location"><Input data-testid="ref-location" value={f.location} onChange={set("location")} /></FF>
                <FF label="Next follow-up"><Input data-testid="ref-followup" type="date" value={f.next_follow_up} onChange={set("next_follow_up")} /></FF>
              </div>
              <FF label="Notes"><Input data-testid="ref-notes" value={f.notes} onChange={set("notes")} /></FF>
              <Button type="submit" data-testid="ref-submit-btn" className="w-full bg-navy hover:bg-navy/90">Add source</Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageTitle>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="refs-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>{["Organization", "Category", "Contact", "Location", "Next Follow-up", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {refs.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`ref-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{r.organization}</td>
                  <td className="px-4 py-3"><Badge variant="secondary">{r.category}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{r.contact}</td>
                  <td className="px-4 py-3 text-slate-600">{r.location}</td>
                  <td className="px-4 py-3 text-slate-600">{r.next_follow_up}</td>
                  <td className="px-4 py-3"><button onClick={() => del(r.id)} data-testid={`ref-del-${r.id}`}><Trash2 className="h-4 w-4 text-slate-400 hover:text-destructive" /></button></td>
                </tr>
              ))}
              {refs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No referral sources yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
function FF({ label, req, children }) {
  return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}{req && " *"}</Label><div className="mt-1">{children}</div></div>;
}
